import { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import UserInteractionsService from "../../services/interactions/Interactions";
import ActionItemService from "../../services/actionItems/ActionItemService";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import { useQueryClient } from "@tanstack/react-query";
import spaceSettingIcon from "../../assets/common/space-settings.svg";
import { capitalizeFirstLetter, COLORS, getAvatarColor, getAvatarInitials } from "../../utils/utils";
import AddMembers from "./AddMembers";
import CreateSpace from "./CreateSpace";
import useGlobalContext from "../../context/useGlobalContext";
import "../../styles/settings.css";
import ClientService from "../../services/clients/ClientService";
import MembersIcon from "../../assets/common/space-members-icon.svg";
import ClientsIcon from "../../assets/common/space-clients-icon.svg";

const Spaces = () => {
  const [openCreate, setOpenCreate] = useState(false);
  const [activeUsers, setActiveUsers] = useState<any[]>([]);
  const [spaces, setSpaces] = useState<any[]>([]);
  const { showSnackbar } = useDisplaySnackbar();
  const [openAddMembers, setOpenAddMembers] = useState(false);
  const [selectedSpace, setSelectedSpace] = useState<any>(null);
  const queryClient = useQueryClient();
  const [openEdit, setOpenEdit] = useState(false);
  const [spaceToEdit, setSpaceToEdit] = useState<any>(null);
  const { accountDetails } = useGlobalContext();
  const roleId = accountDetails?.roleId;
  const isAdmin = roleId?.toLowerCase() === "admin" || roleId?.toLowerCase() === "superaccess";
  const [spaceViews, setSpaceViews] = useState<{ [spaceId: string]: "members" | "clients" }>({});
  const [activeClients, setActiveClients] = useState<any[]>([]);

  const getSpaceView = (spaceId: string): "members" | "clients" => {
    return spaceViews[spaceId] || "members";
  };

  const handleViewMembers = (spaceId: string) => {
    setSpaceViews((prev) => ({ ...prev, [spaceId]: "members" }));
  };

  const handleViewClients = (spaceId: string) => {
    setSpaceViews((prev) => ({ ...prev, [spaceId]: "clients" }));
  };

  useEffect(() => {
    const handleOpen = () => setOpenCreate(true);
    window.addEventListener("openCreateSpace", handleOpen);
    return () => window.removeEventListener("openCreateSpace", handleOpen);
  }, []);

  const {
    isSuccess: isActiveUsersSuccess,
    isLoading: isActiveUsersLoading,
    isError: isActiveUsersError,
    error: activeUsersError,
    data: activeUsersData,
  } = UserInteractionsService.useGetActiveUsers({}, true);

  useEffect(() => {
    if (isActiveUsersSuccess) {
      setActiveUsers(activeUsersData?.data?.data || []);
    }
    if (isActiveUsersError) {
      showSnackbar("error", `Error fetching active users: ${activeUsersError}`);
    }
  }, [isActiveUsersSuccess, isActiveUsersError, activeUsersData, isActiveUsersLoading]);

  const {
    isLoading: isActiveClientsLoading,
    isSuccess: isActiveClientsSuccess,
    isError: isActiveClientsError,
    error: activeClientsError,
    data: activeClientsData,
  } = ClientService.useGetActiveClients(true);

  useEffect(() => {
    if(isActiveClientsSuccess && activeClientsData) {
      setActiveClients(activeClientsData?.data || []);
    }
    if(isActiveClientsError) {
      showSnackbar("error", activeClientsError?.message || "Failed to load active clients.");
    }
  },[isActiveClientsSuccess, isActiveClientsError, activeClientsError, activeClientsData, isActiveClientsLoading])

  const {
    isLoading: isSpacesLoading,
    isSuccess: isSpacesSuccess,
    isError: isSpacesError,
    error: spacesError,
    data: spacesData,
  } = ActionItemService.useGetSpaces(true);

  useEffect(() => {
    if (isSpacesSuccess && spacesData) {
      setSpaces(spacesData?.data || []);
    }
    if (isSpacesError) {
      showSnackbar("error", `Error fetching spaces: ${spacesError}`);
    }
  }, [isSpacesSuccess, isSpacesError, spacesData]);

  const updateSpacesMutation = ActionItemService.useUpdateSpaces({
    onSuccess: () => {
      showSnackbar("success", "Updated successfully");
      setOpenAddMembers(false);
      queryClient.invalidateQueries({ queryKey: ["get_spaces"] });
      queryClient.invalidateQueries({ queryKey: ["get_user_groups"] });
    },
    onError: (err: any) => {
      showSnackbar("error", `Error while updating: ${err?.message || err}`);
    },
  });

  const [addModalType, setAddModalType] = useState<"members" | "clients">("members");

  const handleAddMembersClick = (space: any) => {
    setSelectedSpace(space);
    setAddModalType("members");
    setOpenAddMembers(true);
  };

  const handleAddClientsClick = (space: any) => {
    setSelectedSpace(space);
    setAddModalType("clients");
    setOpenAddMembers(true);
  };

  const handleSaveMembers = (payload: {
    removeMemberProfileOrgIds: string[];
    spaceMemberProfileOrgIds: string[];
    subOrgIds?: string[];
    removeSubOrgIds?: string[];
  }) => {
    const {
      removeMemberProfileOrgIds,
      spaceMemberProfileOrgIds,
      subOrgIds,
      removeSubOrgIds,
    } = payload;

    const updatePayload = {
      description: selectedSpace?.spaceDescription || "",
      spaceId: selectedSpace?.spaceId,
      title: selectedSpace?.spaceName,
      spaceMemberProfileOrgIds,
      removeMemberProfileOrgIds,
      subOrgIds,
      removeSubOrgIds,
    };

    updateSpacesMutation.mutate(updatePayload);
  };

  const handleEditSpace = (space: any) => {
    setSpaceToEdit({
      id: space.spaceId,
      title: space.spaceName,
      spaceMemberProfileOrgIds: space.spaceMembers?.map(
        (m: any) => m.memberProfileOrgId
      ),
      subOrgIds: space.subOrgs?.map((c: any) => c.subOrgId),
    });
    setOpenEdit(true);
  };

  const getColorForName = (name: string) => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % COLORS.length;
    return COLORS[index];
  };

  return (
    <Box className="spaces-main-container">
      {isSpacesLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {spaces.length > 0 ? (
            spaces.map((space: any, index: number) => {
              const activeView = getSpaceView(space.spaceId);
              return (
                <Box key={space.spaceId || index} className="space-container">
                  <Box className="space-header">
                    <Box className="space-header-info">
                      <Box
                        className="space-name"
                        style={{
                          backgroundColor: getColorForName(space.spaceName),
                        }}
                      >
                        {getAvatarInitials(space.spaceName)}
                      </Box>

                      <Box className="space-details">
                        <Box className="space-title-row">
                          <Typography className="space-title">
                            {space.spaceName}
                          </Typography>

                          {isAdmin && (
                            <Box
                              className="space-settings-icon"
                              onClick={() => handleEditSpace(space)}
                            >
                              <img src={spaceSettingIcon} alt="settings" />
                            </Box>
                          )}
                        </Box>

                        <Box className="space-subtitle-row">
                          <Typography
                            className={`space-subtitle-link ${activeView === "members" ? "active" : ""}`}
                            onClick={() => handleViewMembers(space.spaceId)}
                          >
                            {space.spaceMembersCount ?? space.spaceMembers?.length ?? 0} members
                          </Typography>

                          <Typography className="space-subtitle-dot">•</Typography>

                          <Typography
                            className={`space-subtitle-link ${activeView === "clients" ? "active" : ""}`}
                            onClick={() => handleViewClients(space.spaceId)}
                          >
                            {space.subOrgsCount ?? space.subOrgs?.length ?? 0} clients
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  {/* Section Header with Icon */}
                  <Box className="space-section-header">
                    <Box className="space-section-tab">
                      {activeView === "members" ? (
                        <>
                          <img src={MembersIcon} alt="Members" className="space-section-icon" />
                          <Typography className="space-section-title">
                            MEMBERS
                          </Typography>
                        </>
                      ) : (
                        <>
                          <img src={ClientsIcon} alt="Clients" className="space-section-icon" />
                          <Typography className="space-section-title">
                            CLIENTS
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>

                  {/* Section Content */}
                  <Box>
                    {activeView === "members" ? (
                      <Box className="space-members-list">
                        <Grid container spacing={1}>
                        {space.spaceMembers?.map((member: any) => (
                          <Grid item xs="auto" sm={4} md={3} lg={2} key={member.memberProfileOrgId}>
                          <Tooltip title={member?.name || ""} arrow>
                            <Box className="space-member-item">
                              <Avatar
                                src={member?.pictureUrl || ""}
                                alt={member?.name || ""}
                                className="space-member-avatar"
                                style={{
                                  backgroundColor: !member?.pictureUrl ? getAvatarColor(member?.email || member?.name) : undefined,
                                }}
                              >
                                {!member?.pictureUrl && getAvatarInitials(member?.email || member?.name)}
                              </Avatar>
                              <Typography
                                variant="body1"
                                className="space-member-name"
                              >
                                {member?.name}
                              </Typography>
                            </Box>
                          </Tooltip>
                          </Grid>
                        ))}
                        <Grid item xs={2} sx={{alignContent: "center"}}>
                        {isAdmin && (
                          <Button
                            variant="outlined"
                            size="small"
                            endIcon={<AddIcon fontSize="small" />}
                            onClick={() => handleAddMembersClick(space)}
                            className="space-member-add-btn"
                          >
                            Add
                          </Button>
                        )}
                        </Grid>
                        </Grid>
                      </Box>
                    ) : (
                      <Box className="space-clients-list">
                        {space.subOrgs?.map((client: any, idx: number) => (
                          <Chip
                            key={client.subOrgId || client.id || idx}
                            label={capitalizeFirstLetter(client.subOrgName || client.clientName || client.name || "")}
                            variant="outlined"
                            className="space-client-chip"
                          />
                        ))}

                        {isAdmin && (
                          <Button
                            variant="outlined"
                            size="small"
                            endIcon={<AddIcon fontSize="small" />}
                            onClick={() => handleAddClientsClick(space)}
                            className="space-client-add-btn"
                          >
                            Add
                          </Button>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })
          ) : (
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", textAlign: "center", mt: 2 }}
            >
              No spaces found.
            </Typography>
          )}
        </>
      )}

      <CreateSpace
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        activeUsers={activeUsers}
        mode="create"
        activeClients={activeClients}
      />

      <CreateSpace
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        onSuccess={() => {
          setOpenEdit(false);
        }}
        activeUsers={activeUsers}
        mode="edit"
        spaceData={spaceToEdit}
        activeClients={activeClients}
      />

      <AddMembers
        open={openAddMembers}
        onClose={() => setOpenAddMembers(false)}
        onSave={handleSaveMembers}
        activeUsers={activeUsers}
        activeClients={activeClients}
        targetType={addModalType}
        existingMembers={
          selectedSpace?.spaceMembers?.map((m: any) => ({
            id: m.memberProfileOrgId,
            name: m.name,
            email: m.email,
            pictureUrl: m.pictureUrl,
            profileOrgRelationshipId: m.memberProfileOrgId,
          })) || []
        }
        existingClients={selectedSpace?.subOrgs || []}
        spaceName={selectedSpace?.spaceName || ""}
      />
    </Box>
  );
};

export default Spaces;